import { LIMITS } from "./constants.js";

/**
 * RFC 5321/5322 address handling. Pure, deterministic, dependency-free.
 *
 * The accepted grammar is deliberately narrower than the RFC (no quoted local
 * parts, no address literals): MailFlare only routes addresses it can safely
 * round-trip through DNS and `Email Routing`.
 */

export interface CanonicalAddress {
  /** Normalized `local@domain`, both parts lowercased for matching. */
  readonly address: string;
  readonly localPart: string;
  readonly domain: string;
}

/** Unquoted `dot-atom` local part, per RFC 5322 §3.4.1. */
const LOCAL_PART_PATTERN = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;
const DOMAIN_LABEL_PATTERN = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;

/** True when `value` is a syntactically valid, routable email address. */
export function isEmailAddress(value: string): boolean {
  if (value.length === 0 || value.length > LIMITS.maxAddressLength) return false;
  const at = value.lastIndexOf("@");
  if (at <= 0 || at === value.length - 1) return false;

  const localPart = value.slice(0, at);
  const domain = value.slice(at + 1);
  if (localPart.length > LIMITS.maxLocalPartLength) return false;
  if (domain.length > LIMITS.maxDomainLength) return false;
  if (!LOCAL_PART_PATTERN.test(localPart)) return false;
  if (localPart.startsWith(".") || localPart.endsWith(".") || localPart.includes("..")) {
    return false;
  }

  const labels = domain.split(".");
  if (labels.length < 2) return false;
  const tld = labels[labels.length - 1];
  if (tld === undefined || tld.length < 2 || !/^[A-Za-z]{2,63}$/.test(tld)) return false;
  return labels.every((label) => label.length > 0 && DOMAIN_LABEL_PATTERN.test(label));
}

/** Returns the lowercased `local@domain` form, or `null` if invalid. */
export function canonicalizeAddress(value: string): CanonicalAddress | null {
  const trimmed = value.trim();
  if (!isEmailAddress(trimmed)) return null;
  const at = trimmed.lastIndexOf("@");
  const localPart = trimmed.slice(0, at).toLowerCase();
  const domain = trimmed.slice(at + 1).toLowerCase();
  return { address: `${localPart}@${domain}`, localPart, domain };
}

/** Extracts the domain from a valid address, or `null`. */
export function domainOf(value: string): string | null {
  return canonicalizeAddress(value)?.domain ?? null;
}

/** Normalizes a `From`/`To` header value, preserving display names. */
export function parseAddressHeader(value: string): readonly { name?: string; address: string }[] {
  const parts = splitAddressList(value);
  const out: { name?: string; address: string }[] = [];
  for (const part of parts) {
    const angleMatch = /^(.*?)<([^>]+)>$/.exec(part.trim());
    if (angleMatch) {
      const rawName = angleMatch[1] ?? "";
      const rawAddress = angleMatch[2] ?? "";
      const canonical = canonicalizeAddress(rawAddress);
      if (canonical) {
        const name = unquote(rawName.trim());
        out.push(
          name.length > 0 ? { name, address: canonical.address } : { address: canonical.address },
        );
      }
      continue;
    }
    const canonical = canonicalizeAddress(part.trim());
    if (canonical) out.push({ address: canonical.address });
  }
  return out;
}

function splitAddressList(value: string): readonly string[] {
  const parts: string[] = [];
  let current = "";
  let inQuotes = false;
  for (const char of value) {
    if (char === '"') inQuotes = !inQuotes;
    if (char === "," && !inQuotes) {
      parts.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  if (current.trim().length > 0) parts.push(current);
  return parts;
}

function unquote(value: string): string {
  if (value.startsWith('"') && value.endsWith('"') && value.length >= 2) {
    return value.slice(1, -1);
  }
  return value;
}
